// src/components/aiinsight/ReportBrandingPanel.jsx
// Modern floating side panel for report branding and layout customization.
// Replaces the inline "double-click" editing with a professional Canva/Notion-style UX.

import React, { useState } from 'react';
import {
    Box, Drawer, Typography, IconButton, Tabs, Tab, Divider,
    Switch, FormControlLabel, Slider, TextField, Button,
    ToggleButtonGroup, ToggleButton, Tooltip, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import PaletteIcon from '@mui/icons-material/Palette';
import SettingsIcon from '@mui/icons-material/Settings';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import { DEFAULT_HEADER_CONFIG } from './EditableHeaderBlock';
import { DEFAULT_FOOTER_CONFIG } from './EditableFooterBlock';

const TabPanel = ({ children, value, index }) => (
    <Box hidden={value !== index} sx={{ p: 2.5, height: 'calc(100% - 48px)', overflowY: 'auto' }}>
        {value === index && children}
    </Box>
);

const SectionTitle = ({ children }) => (
    <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', mb: 2, mt: 1 }}>
        {children}
    </Typography>
);

const ReportBrandingPanel = ({
    open,
    onClose,
    headerConfig,
    setHeaderConfig,
    footerConfig,
    setFooterConfig,
    onReset,
    onSaveDefault,
    onApplyAll,
}) => {
    const [tab, setTab] = useState(0);

    const updateHeader = (patch) => setHeaderConfig({ ...headerConfig, ...patch });
    const updateFooter = (patch) => setFooterConfig({ ...footerConfig, ...patch });

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: 380,
                    borderLeft: '1px solid #e2e8f0',
                    boxShadow: '-12px 0 40px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                }
            }}
        >
            {/* Header */}
            <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#1e293b' }}>
                        Report Branding & Layout
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: '#94a3b8' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Tabs */}
            <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                variant="fullWidth"
                sx={{
                    minHeight: 48,
                    borderBottom: '1px solid #f1f5f9',
                    '& .MuiTab-root': {
                        fontSize: 12, fontWeight: 700, textTransform: 'none', color: '#64748b',
                        '&.Mui-selected': { color: '#6366f1' }
                    },
                    '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' }
                }}
            >
                <Tab label="Header" />
                <Tab label="Footer" />
                <Tab label="Style" />
            </Tabs>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {/* ── HEADER TAB ── */}
                <TabPanel value={tab} index={0}>
                    <SectionTitle>Content & Visibility</SectionTitle>
                    <Stack spacing={2.5}>
                        <FormControlLabel
                            control={<Switch size="small" checked={headerConfig.showLogo} onChange={e => updateHeader({ showLogo: e.target.checked })} />}
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Show Tenant Logo</Typography>}
                        />
                        <FormControlLabel
                            control={<Switch size="small" checked={headerConfig.showCompanyName} onChange={e => updateHeader({ showCompanyName: e.target.checked })} />}
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Show Company Name</Typography>}
                        />
                        {headerConfig.showCompanyName && (
                            <TextField
                                label="Custom Company Name"
                                size="small"
                                fullWidth
                                value={headerConfig.companyName || ''}
                                onChange={e => updateHeader({ companyName: e.target.value })}
                                sx={{ '& .MuiInputBase-input': { fontSize: 12.5 } }}
                            />
                        )}
                        <FormControlLabel
                            control={<Switch size="small" checked={headerConfig.showSubtitle} onChange={e => updateHeader({ showSubtitle: e.target.checked })} />}
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Show Subtitle</Typography>}
                        />
                        {headerConfig.showSubtitle && (
                            <TextField
                                label="Subtitle Text"
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                value={headerConfig.subtitle || ''}
                                onChange={e => updateHeader({ subtitle: e.target.value })}
                                sx={{ '& .MuiInputBase-input': { fontSize: 12.5 } }}
                            />
                        )}
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>Layout & Position</SectionTitle>
                    <Stack spacing={3}>
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1, color: '#64748b' }}>Alignment</Typography>
                            <ToggleButtonGroup
                                value={headerConfig.align}
                                exclusive
                                onChange={(e, v) => v && updateHeader({ align: v })}
                                size="small"
                                fullWidth
                            >
                                <ToggleButton value="left"><AlignHorizontalLeftIcon sx={{ fontSize: 18 }} /></ToggleButton>
                                <ToggleButton value="center"><AlignHorizontalCenterIcon sx={{ fontSize: 18 }} /></ToggleButton>
                                <ToggleButton value="right"><AlignHorizontalRightIcon sx={{ fontSize: 18 }} /></ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1, color: '#64748b' }}>Arrangement</Typography>
                            <ToggleButtonGroup
                                value={headerConfig.layout}
                                exclusive
                                onChange={(e, v) => v && updateHeader({ layout: v })}
                                size="small"
                                fullWidth
                            >
                                <ToggleButton value="stack" sx={{ gap: 1 }}><ViewHeadlineIcon sx={{ fontSize: 18 }} /> Stacked</ToggleButton>
                                <ToggleButton value="inline" sx={{ gap: 1 }}><ViewColumnIcon sx={{ fontSize: 18 }} /> Inline</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Stack>
                </TabPanel>

                {/* ── FOOTER TAB ── */}
                <TabPanel value={tab} index={1}>
                    <SectionTitle>Content Toggles</SectionTitle>
                    <Stack spacing={2.5}>
                        <FormControlLabel
                            control={<Switch size="small" checked={footerConfig.showFooter} onChange={e => updateFooter({ showFooter: e.target.checked })} />}
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Enable Footer</Typography>}
                        />
                        <FormControlLabel
                            control={<Switch size="small" checked={footerConfig.showCompanyName} onChange={e => updateFooter({ showCompanyName: e.target.checked })} />}
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Show Company Name</Typography>}
                        />
                        <FormControlLabel
                            control={<Switch size="small" checked={footerConfig.showDate} onChange={e => updateFooter({ showDate: e.target.checked })} />}
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Show Generation Date</Typography>}
                        />

                        <TextField
                            label="Custom Footer Text"
                            size="small"
                            fullWidth
                            placeholder="Add disclaimer or notes..."
                            value={footerConfig.customText || ''}
                            onChange={e => updateFooter({ customText: e.target.value })}
                            sx={{ mt: 1, '& .MuiInputBase-input': { fontSize: 12.5 } }}
                        />
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>Footer Alignment</SectionTitle>
                    <ToggleButtonGroup
                        value={footerConfig.align || 'center'}
                        exclusive
                        onChange={(e, v) => v && updateFooter({ align: v })}
                        size="small"
                        fullWidth
                    >
                        <ToggleButton value="left"><AlignHorizontalLeftIcon sx={{ fontSize: 18 }} /></ToggleButton>
                        <ToggleButton value="center"><AlignHorizontalCenterIcon sx={{ fontSize: 18 }} /></ToggleButton>
                        <ToggleButton value="right"><AlignHorizontalRightIcon sx={{ fontSize: 18 }} /></ToggleButton>
                    </ToggleButtonGroup>
                </TabPanel>

                {/* ── STYLE TAB ── */}
                <TabPanel value={tab} index={2}>
                    <SectionTitle>Layout Spacing</SectionTitle>
                    <Stack spacing={2}>
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Header Bottom Padding</Typography>
                                <Typography sx={{ fontSize: 12, color: '#6366f1' }}>{headerConfig.paddingBottom ?? 2}px</Typography>
                            </Box>
                            <Slider
                                size="small"
                                value={headerConfig.paddingBottom ?? 2}
                                min={0} max={10}
                                onChange={(e, v) => updateHeader({ paddingBottom: v })}
                            />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Footer Top Padding</Typography>
                                <Typography sx={{ fontSize: 12, color: '#6366f1' }}>{footerConfig.paddingTop ?? 3}px</Typography>
                            </Box>
                            <Slider
                                size="small"
                                value={footerConfig.paddingTop ?? 3}
                                min={0} max={10}
                                onChange={(e, v) => updateFooter({ paddingTop: v })}
                            />
                        </Box>
                        <FormControlLabel
                            control={<Switch size="small" checked={headerConfig.showDivider} onChange={e => updateHeader({ showDivider: e.target.checked })} />}
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Show Header Divider</Typography>}
                        />
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>Global Styles</SectionTitle>
                    <Stack spacing={2.5}>
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1, color: '#64748b' }}>Divider Style</Typography>
                            <ToggleButtonGroup
                                value={headerConfig.dividerStyle || 'solid'}
                                exclusive
                                onChange={(e, v) => v && updateHeader({ dividerStyle: v })}
                                size="small"
                                fullWidth
                            >
                                <ToggleButton value="solid" sx={{ fontSize: 11 }}>Solid</ToggleButton>
                                <ToggleButton value="dashed" sx={{ fontSize: 11 }}>Dashed</ToggleButton>
                                <ToggleButton value="dotted" sx={{ fontSize: 11 }}>Dotted</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Divider Color</Typography>
                            <input
                                type="color"
                                value={headerConfig.dividerColor || '#e2e8f0'}
                                onChange={e => updateHeader({ dividerColor: e.target.value })}
                                style={{ width: 40, height: 24, border: 'none', borderRadius: 4, cursor: 'pointer' }}
                            />
                        </Box>
                    </Stack>
                </TabPanel>
            </Box>

            {/* Footer Actions */}
            <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        startIcon={<RestartAltIcon />}
                        onClick={onReset}
                        sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, borderColor: '#e2e8f0', color: '#64748b' }}
                    >
                        Reset
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={onSaveDefault}
                        sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, borderColor: '#e2e8f0', color: '#64748b' }}
                    >
                        Save
                    </Button>
                </Box>
                <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    startIcon={<DoneAllIcon />}
                    onClick={onApplyAll}
                    sx={{
                        textTransform: 'none', fontSize: 12, fontWeight: 700,
                        bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' },
                        boxShadow: '0 4px 12px rgba(99,102,241,0.2)'
                    }}
                >
                    Apply to All Reports
                </Button>
            </Box>
        </Drawer>
    );
};

export default ReportBrandingPanel;
